import { forwardRef, ComponentPropsWithoutRef } from "react";
import cx from "classnames";

import styles from "./Link.module.scss";
import { Icon } from "../Icon";
import OpenInNew from "@servicetitan/hammer-icon/mdi/round/open_in_new.svg";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

/**
 * Props for the Link component.
 * @extends ComponentPropsWithoutRef<"a">
 * @extends LayoutUtilProps
 */
export type LinkProps = ComponentPropsWithoutRef<"a"> &
  LayoutUtilProps &
  (
    | {
        /**
         * The visual appearance of the link.
         * @default "primary"
         */
        appearance?: "primary";
        /** Whether the link should have a quieter, less prominent appearance */
        quiet?: boolean;
      }
    | {
        /** The visual appearance of the link. Secondary appearance cannot be quiet. */
        appearance: "secondary";
        quiet?: false;
      }
  );

/**
 * Link component for creating accessible, styled hyperlinks.
 *
 * Features:
 * - Two visual appearances: primary (default) and secondary
 * - Quiet mode for less prominent styling
 * - Automatic external link icon for target="_blank" links
 * - Security attributes (rel="noopener noreferrer") for external links
 * - Full accessibility support with proper focus management
 * - Supports layout utilities for positioning and spacing
 * - Flexible content support for text and other elements
 * - Responsive design with consistent typography
 * - Hover and focus states with proper visual feedback
 * - Screen reader support for external link announcements
 *
 * @example
 * <Link href="/dashboard" appearance="primary">
 *   Go to Dashboard
 * </Link>
 *
 * @example
 * <Link href="https://example.com" target="_blank" quiet>
 *   External Documentation
 * </Link>
 *
 * @example
 * <Link href="/settings" appearance="secondary">
 *   Settings
 * </Link>
 *
 * @example
 * <Link href="/profile" gap="2" alignItems="center">
 *   <Avatar name="John Doe" size="small" />
 *   View Profile
 * </Link>
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    children,
    className,
    appearance = "primary",
    quiet,
    style,
    ...rest
  } = componentProps;
  const linkClassNames = cx([styles["link"]], className, {
    [styles["quiet"]]: quiet,
    [styles["secondary"]]: appearance === "secondary",
  });

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  return (
    <a
      className={linkClassNames}
      rel="noopener noreferrer"
      data-anv="link"
      ref={ref}
      style={styleCombined}
      {...rest}
    >
      {children}
      {rest.target === "_blank" && (
        <Icon className={styles["open-in-new"]} inherit svg={OpenInNew} />
      )}
    </a>
  );
});

Link.displayName = "Link";

/**
 * Props for the useLinkStyles hook.
 * @extends LayoutUtilProps
 */
export type UseLinkStylesProps = LayoutUtilProps &
  Pick<LinkProps, "className" | "appearance" | "quiet" | "style">;

/**
 * Hook for generating link styles and class names.
 *
 * @param props - The link style properties
 * @returns Object containing linkClassNames and styleCombined
 */
export const useLinkStyles = (props: UseLinkStylesProps) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const { className, appearance = "primary", quiet, style } = componentProps;

  const linkClassNames = cx([styles["link"]], className, {
    [styles["quiet"]]: quiet,
    [styles["secondary"]]: appearance === "secondary",
  });

  const styleCombined = Object.assign({}, style, layoutStyles);

  return { linkClassNames, styleCombined };
};
